import { PAGE_HEADER_HEIGHT } from '@/catalog-apps/components/config'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/shadcn/collapsible'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/shadcn/sidebar'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { IconRightChevronOutline } from '@icons/IconRightChevronOutline'
import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router'
import type { DocsNavNode } from './docsTypes'
import { docsPath, normalizeDocsPathname } from './docsPaths'
import { useDocsManifest } from './useDocsManifest'

function isSlugExactlyActive(pathname: string, slug: string) {
  const target = normalizeDocsPathname(docsPath(slug))
  const normalized = normalizeDocsPathname(pathname)
  return normalized === target
}

function nodeContainsActive(pathname: string, node: DocsNavNode): boolean {
  if (node.slug !== undefined && isSlugExactlyActive(pathname, node.slug)) {
    return true
  }
  return (node.children ?? []).some((child) => nodeContainsActive(pathname, child))
}

function NavLabel({
  title,
  isActive,
  sidebarOpen,
  showChevron,
  isOpen = false,
}: {
  title: string
  isActive: boolean
  sidebarOpen: boolean
  showChevron: boolean
  isOpen?: boolean
}) {
  return (
    <div
      className={clsx('relative flex w-full min-w-0 items-start gap-1 transition-[max-width] duration-300 ease-in-out', {
        'max-w-[36px]': !sidebarOpen,
      })}
    >
      {isActive && (
        <div
          className={clsx('pointer-events-none absolute inset-y-0 left-0 rounded', {
            'w-[calc(100%-8px)]': sidebarOpen,
            'w-[calc(100%-16px)]': !sidebarOpen,
          })}
        >
          <div className="absolute left-0 top-[calc(50%-12px)] h-[24px] w-[4.5px] rounded-full bg-foreground" />
        </div>
      )}
      <span
        className={clsx(
          'relative z-[1] min-w-0 flex-1 whitespace-normal break-words pl-3 font-light leading-snug transition-opacity duration-200',
          {
            invisible: !sidebarOpen,
            '!font-bold tracking-tighter text-foreground': isActive,
          }
        )}
      >
        {title}
      </span>
      {showChevron && (
        <span className={clsx('ml-auto shrink-0 self-center pl-1', { invisible: !sidebarOpen })}>
          <IconRightChevronOutline
            className={clsx('size-3 transition-transform duration-200', {
              'rotate-90': isOpen,
            })}
          />
        </span>
      )}
    </div>
  )
}

function nestedMenuClassName(level: number) {
  return clsx('pt-1 border-l border-border/40', level > 0 ? 'ml-3 pl-3' : 'ml-2 pl-3')
}

function DocsNavBranch({
  node,
  level,
  sidebarOpen,
  pathname,
  openSections,
  onToggleSection,
}: {
  node: DocsNavNode
  level: number
  sidebarOpen: boolean
  pathname: string
  openSections: Set<string>
  onToggleSection: (key: string, open: boolean) => void
}) {
  const sectionKey = `${level}:${node.title}`
  const hasChildren = Boolean(node.children?.length)
  const slug = node.slug
  const isActive = slug !== undefined ? isSlugExactlyActive(pathname, slug) : false
  const containsActive = nodeContainsActive(pathname, node)
  const isOpen = openSections.has(sectionKey) || containsActive

  useEffect(() => {
    if (containsActive && !openSections.has(sectionKey)) {
      onToggleSection(sectionKey, true)
    }
  }, [containsActive, onToggleSection, openSections, sectionKey])

  const MenuButton = level === 0 ? SidebarMenuButton : SidebarMenuSubButton
  const MenuItem = level === 0 ? SidebarMenuItem : SidebarMenuSubItem

  if (!hasChildren) {
    if (slug === undefined) {
      return null
    }

    return (
      <MenuItem className={clsx({ 'mt-2': level === 0 })}>
        <TooltipButton tooltip={node.title} side="right" disabled={sidebarOpen}>
          <MenuButton asChild isActive={isActive} className="relative h-auto min-h-8 min-w-0 items-start py-1">
            <NavLink to={docsPath(slug)} end aria-current={isActive ? 'page' : undefined}>
              <NavLabel title={node.title} isActive={isActive} sidebarOpen={sidebarOpen} showChevron={false} />
            </NavLink>
          </MenuButton>
        </TooltipButton>
      </MenuItem>
    )
  }

  return (
    <Collapsible
      open={sidebarOpen && isOpen}
      onOpenChange={(open) => onToggleSection(sectionKey, open)}
      className="group/collapsible"
    >
      <MenuItem className={clsx({ 'mt-2': level === 0 })}>
        <TooltipButton tooltip={node.title} side="right" disabled={sidebarOpen}>
          <CollapsibleTrigger asChild>
            <MenuButton isActive={false} className="relative h-auto min-h-8 w-full min-w-0 items-start py-1">
              <NavLabel title={node.title} isActive={false} sidebarOpen={sidebarOpen} showChevron isOpen={isOpen} />
            </MenuButton>
          </CollapsibleTrigger>
        </TooltipButton>
        <CollapsibleContent className="overflow-hidden text-sm transition-all data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          <SidebarMenuSub className={nestedMenuClassName(level)}>
            {node.children?.map((child) => (
              <DocsNavBranch
                key={`${child.title}-${child.slug ?? 'section'}`}
                node={child}
                level={level + 1}
                sidebarOpen={sidebarOpen}
                pathname={pathname}
                openSections={openSections}
                onToggleSection={onToggleSection}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </MenuItem>
    </Collapsible>
  )
}

export function DocsNavMenu({ sidebarOpen = true }: { sidebarOpen?: boolean }) {
  const { data, isLoading, isError } = useDocsManifest()
  const location = useLocation()
  const [openSections, setOpenSections] = useState<Set<string>>(() => new Set())

  const onToggleSection = useMemo(
    () => (key: string, open: boolean) => {
      setOpenSections((current) => {
        const next = new Set(current)
        if (open) {
          next.add(key)
        } else {
          next.delete(key)
        }
        return next
      })
    },
    []
  )

  if (isLoading) {
    return (
      <div style={{ paddingTop: PAGE_HEADER_HEIGHT }} className="px-2">
        <LoadingComponent text="Loading docs..." />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div style={{ paddingTop: PAGE_HEADER_HEIGHT }} className="px-3 text-sm text-muted-foreground">
        Documentation unavailable. Run <code className="text-xs">pnpm build:docs</code> locally.
      </div>
    )
  }

  return (
    <nav style={{ paddingTop: PAGE_HEADER_HEIGHT }} className="pr-2">
      <SidebarMenu>
        {data.nav.map((node) => (
          <DocsNavBranch
            key={`${node.title}-${node.slug ?? 'root'}`}
            node={node}
            level={0}
            sidebarOpen={sidebarOpen}
            pathname={location.pathname}
            openSections={openSections}
            onToggleSection={onToggleSection}
          />
        ))}
      </SidebarMenu>
    </nav>
  )
}
